import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';

const PrivacyPolicy = ({ show, onHide, isPage = false }) => {
  const navigate = useNavigate();

  const handleClose = () => {
    if (isPage) {
      navigate(-1);
    } else {
      onHide();
    }
  };

  const content = (
    <>
      <div className="text-center mb-4">
        <h4 className="text-warning">Taxi-Local.uk Ltd</h4>
        <p className="text-muted">Privacy Policy</p>
      </div>
      
      <div className="mb-4">
        <p className="text-muted small">
          <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-GB')}
        </p>
        <p>
          At Taxi-Local.uk Ltd, we are committed to protecting your privacy and personal data. 
          This Privacy Policy explains how we collect, use, store, and protect your information 
          in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data 
          Protection Act 2018.
        </p>
      </div>
      
      <div className="mb-4">
        <h5>1. Information We Collect</h5>
        <h6>1.1 Information You Provide</h6>
        <ul>
          <li><strong>Account Information:</strong> Name, email address, phone number, password, and profile details</li>
          <li><strong>Driver Information:</strong> License details, vehicle information, insurance details, and professional credentials</li>
          <li><strong>Booking Information:</strong> Pickup and destination addresses, booking times, passenger details, and special requirements</li>
          <li><strong>Payment Information:</strong> Payment method details, transaction history, and billing information</li>
          <li><strong>Communication:</strong> Messages, reviews, feedback, and correspondence with us or other users</li>
        </ul>
        
        <h6>1.2 Information We Collect Automatically</h6>
        <ul>
          <li><strong>Device Information:</strong> Device type, operating system, browser type, IP address, and unique device identifiers</li>
          <li><strong>Location Data:</strong> GPS coordinates, location history, and route information (when you enable location services)</li>
          <li><strong>Usage Data:</strong> How you interact with our platform, pages visited, features used, and time spent</li>
          <li><strong>Cookies and Similar Technologies:</strong> See our Cookie Policy for detailed information</li>
        </ul>
      </div>
      
      <div className="mb-4">
        <h5>2. How We Use Your Information</h5>
        <p>We use your personal data for the following purposes:</p>
        <ul>
          <li><strong>Service Provision:</strong> To provide, maintain, and improve our taxi booking and dispatch services</li>
          <li><strong>Account Management:</strong> To create and manage your account, verify your identity, and process transactions</li>
          <li><strong>Job Matching:</strong> To connect drivers with customers, match jobs based on location and availability</li>
          <li><strong>Communication:</strong> To send booking confirmations, updates, notifications, and respond to your inquiries</li>
          <li><strong>Payment Processing:</strong> To process payments, manage invoices, and handle financial transactions</li>
          <li><strong>Safety and Security:</strong> To ensure platform security, prevent fraud, and maintain service quality</li>
          <li><strong>Legal Compliance:</strong> To comply with legal obligations, regulatory requirements, and licensing bodies</li>
          <li><strong>Analytics and Improvement:</strong> To analyze usage patterns, improve our services, and develop new features</li>
          <li><strong>Marketing:</strong> To send promotional communications (with your consent, which you can withdraw at any time)</li>
        </ul>
      </div>
      
      <div className="mb-4">
        <h5>3. Legal Basis for Processing</h5>
        <p>We process your personal data based on the following legal grounds:</p>
        <ul>
          <li><strong>Contract Performance:</strong> To fulfill our contractual obligations to provide our services</li>
          <li><strong>Legitimate Interests:</strong> To operate our business, ensure security, and improve our services</li>
          <li><strong>Legal Obligations:</strong> To comply with laws and regulations, including licensing requirements</li>
          <li><strong>Consent:</strong> For marketing communications and optional features (you can withdraw consent anytime)</li>
        </ul>
      </div>
      
      <div className="mb-4">
        <h5>4. How We Share Your Information</h5>
        <p>We may share your information with:</p>
        <ul>
          <li><strong>Drivers and Customers:</strong> To facilitate bookings and communications (e.g., sharing contact details for job completion)</li>
          <li><strong>Service Providers:</strong> Third-party companies that help us operate our platform (payment processors, cloud hosting, analytics)</li>
          <li><strong>Licensing Bodies:</strong> When required by law or licensing regulations</li>
          <li><strong>Legal Authorities:</strong> When required by law, court order, or to protect rights and safety</li>
          <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets (with notice to users)</li>
        </ul>
        <p>We do not sell your personal data to third parties for marketing purposes.</p>
      </div>
      
      <div className="mb-4">
        <h5>5. Data Storage and Security</h5>
        <ul>
          <li><strong>Storage Location:</strong> Your data is stored on secure servers, primarily within the UK and EEA</li>
          <li><strong>Security Measures:</strong> We use industry-standard security measures including encryption, secure connections (HTTPS), and access controls</li>
          <li><strong>Data Retention:</strong> We retain your data only as long as necessary for the purposes outlined in this policy, or as required by law</li>
          <li><strong>Account Deletion:</strong> You can request account deletion at any time, subject to legal retention requirements</li>
        </ul>
      </div>
      
      <div className="mb-4">
        <h5>6. Your Rights Under UK GDPR</h5>
        <p>You have the following rights regarding your personal data:</p>
        <ul>
          <li><strong>Right to Access:</strong> Request a copy of the personal data we hold about you</li>
          <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
          <li><strong>Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
          <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
          <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
          <li><strong>Right to Object:</strong> Object to processing based on legitimate interests or for marketing purposes</li>
          <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for processing that relies on consent</li>
        </ul>
        <p>To exercise these rights, please contact us using the details provided below.</p>
      </div>
      
      <div className="mb-4">
        <h5>7. Cookies and Tracking Technologies</h5>
        <p>
          We use cookies and similar technologies to enhance your experience, analyze usage, and provide 
          personalized features. For detailed information about our use of cookies, please see our{' '}
          <Link to="/cookies-policy" className="text-decoration-none">Cookie Policy</Link>.
        </p>
      </div>
      
      <div className="mb-4">
        <h5>8. Location Data</h5>
        <p>
          Location data is collected to provide location-based services such as job matching and route optimization. 
          You can control location permissions through your device settings. Location data is only shared with 
          relevant parties (e.g., drivers for job assignments) and is retained only as long as necessary.
        </p>
      </div>
      
      <div className="mb-4">
        <h5>9. Children's Privacy</h5>
        <p>
          Our services are not intended for individuals under 18 years of age. We do not knowingly collect 
          personal data from children. If you believe we have collected information from a child, please 
          contact us immediately.
        </p>
      </div>
      
      <div className="mb-4">
        <h5>10. Third-Party Links</h5>
        <p>
          Our platform may contain links to third-party websites or services. We are not responsible for 
          the privacy practices of these external sites. We encourage you to review their privacy policies.
        </p>
      </div>
      
      <div className="mb-4">
        <h5>11. International Data Transfers</h5>
        <p>
          Your data is primarily stored and processed within the UK and EEA. If we transfer data outside 
          these areas, we ensure appropriate safeguards are in place to protect your data in accordance 
          with UK GDPR requirements.
        </p>
      </div>
      
      <div className="mb-4">
        <h5>12. Changes to This Privacy Policy</h5>
        <p>
          We may update this Privacy Policy periodically to reflect changes in our practices or legal 
          requirements. We will notify you of significant changes by email or through our platform. 
          Continued use of our services after changes constitutes acceptance of the updated policy.
        </p>
      </div>
      
      <div className="mb-4">
        <h5>13. Contact Us</h5>
        <p>
          If you have questions, concerns, or wish to exercise your rights regarding your personal data, 
          please contact us:
        </p>
        <p>
          <strong>Taxi-Local.uk Ltd</strong><br/>
          Email: privacy@taxi-local.uk<br/>
          Data Protection Officer: dpo@taxi-local.uk<br/>
          Website: www.taxi-local.uk
        </p>
        <p>
          You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) 
          if you believe we have not handled your personal data in accordance with data protection laws:
        </p>
        <p>
          <strong>Information Commissioner's Office</strong><br/>
          Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">www.ico.org.uk</a><br/>
          Telephone: 0303 123 1113
        </p>
      </div>
      
      <div className="text-muted small">
        <p>This Privacy Policy is governed by English law and subject to the jurisdiction of English courts.</p>
      </div>
    </>
  );

  if (isPage) {
    return (
      <div className="container mt-4 mb-5">
        <div className="row">
          <div className="col-lg-10 mx-auto">
            <div className="card">
              <div className="card-header bg-warning">
                <div className="d-flex justify-content-between align-items-center">
                  <h3 className="mb-0">🔒 Privacy Policy</h3>
                  <Button variant="outline-dark" onClick={handleClose}>
                    ← Back
                  </Button>
                </div>
              </div>
              <div className="card-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                {content}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>🔒 Privacy Policy</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {content}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PrivacyPolicy;

